import { useState } from "react";
import { Plus, ListChecks, Trash2, Circle, CheckCircle2, Clock } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, EmptyState, Progress, PageSkeleton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const PERIODS = ["12+ months", "9-12 months", "6-9 months", "4-6 months", "3 months", "2 months", "1 month", "2 weeks", "1 week", "Wedding day", "After wedding"];
const PRIORITIES = ["High", "Medium", "Low"];
const STATUSES = ["Not Started", "In Progress", "Completed"];

export default function Timeline() {
  const { items, loading, create, update, remove } = useCollection("tasks");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [filter, setFilter] = useState("all");

  if (loading) return <PageSkeleton />;

  const completed = items.filter((t) => t.status === "Completed").length;
  const pct = items.length ? Math.round(completed / items.length * 100) : 0;
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (t) => t.status !== "Completed" && t.due_date && t.due_date.slice(0, 10) < today;

  const save = async () => {
    const payload = { title: (form.title || "").trim(), category: form.category || "", period: form.period || "12+ months",
      due_date: form.due_date || "", priority: form.priority || "Medium", status: form.status || "Not Started", notes: form.notes || "" };
    if (!payload.title) { toast.error("Please enter a task."); return; }
    await create(payload); toast.success("Task added"); setOpen(false); setForm({});
  };

  const toggle = (t) => { const next = t.status === "Completed" ? "Not Started" : "Completed"; update(t.id, { status: next }); };

  const filtered = items.filter((t) => filter === "all" ? true : filter === "overdue" ? isOverdue(t) : filter === "done" ? t.status === "Completed" : t.status !== "Completed");
  const byPeriod = PERIODS.map((p) => ({ period: p, tasks: filtered.filter((t) => (t.period || "12+ months") === p) })).filter((g) => g.tasks.length);

  return (
    <div>
      <PageHeader title="Wedding Timeline" subtitle="A guided checklist so nothing slips through the cracks." testId="timeline-header"
        action={<Button onClick={() => { setForm({}); setOpen(true); }} className="rounded-full" data-testid="add-task"><Plus className="h-4 w-4 mr-1.5" />Add Task</Button>} />

      <div className="rounded-2xl bg-card border border-border p-6 mb-6">
        <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Overall progress</span><span className="tnum font-medium">{completed}/{items.length} · {pct}%</span></div>
        <Progress value={pct} />
      </div>

      <div className="flex gap-1.5 mb-6">
        {[["all", "All"], ["open", "To do"], ["overdue", "Overdue"], ["done", "Completed"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`tfilter-${k}`} className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === k ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/70"}`}>{l}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={ListChecks} title="No tasks yet" description="Add your first task or load the demo to see a full planning timeline." actionLabel="Add Task" onAction={() => setOpen(true)} testId="timeline-empty" />
      ) : (
        <div className="space-y-8">
          {byPeriod.map((g) => (
            <div key={g.period}>
              <h3 className="text-lg tracking-tight text-primary mb-3 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent" />{g.period} before</h3>
              <div className="space-y-2">
                {g.tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-xl bg-card border border-border px-4 py-3" data-testid="task-row">
                    <button onClick={() => toggle(t)} data-testid="toggle-task" className="shrink-0">{t.status === "Completed" ? <CheckCircle2 className="h-5 w-5 text-accent" /> : <Circle className="h-5 w-5 text-muted-foreground" />}</button>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${t.status === "Completed" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                        {t.category && <span>{t.category}</span>}
                        {t.due_date && <span className={isOverdue(t) ? "text-destructive font-medium" : ""}><Clock className="h-3 w-3 inline mr-0.5" />{formatDate(t.due_date)}{isOverdue(t) ? " · overdue" : ""}</span>}
                        <span className={`px-2 py-0.5 rounded-full ${t.priority === "High" ? "bg-destructive/10 text-destructive" : "bg-secondary"}`}>{t.priority}</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => { remove(t.id); toast.success("Task deleted"); }} data-testid="delete-task"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">Add task</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Task</Label><Input value={form.title || ""} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="rounded-xl mt-1" data-testid="task-title" /></div>
            <div><Label className="text-xs text-muted-foreground">Category</Label><Input value={form.category || ""} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Period</Label>
              <Select value={form.period || "12+ months"} onValueChange={(v) => setForm((p) => ({ ...p, period: v }))}><SelectTrigger className="rounded-xl mt-1" data-testid="task-period"><SelectValue /></SelectTrigger><SelectContent>{PERIODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Priority</Label>
              <Select value={form.priority || "Medium"} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}><SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={form.status || "Not Started"} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}><SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button><Button className="rounded-full" onClick={save} data-testid="save-task">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
